import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';


// Types based on 17-frontend-chit-group-management.md

export type SchemeStatus =
  | 'DRAFT' | 'PENDING_REGISTRATION' | 'REGISTERED' | 'READY'
  | 'ACTIVE' | 'SUSPENDED' | 'CLOSING' | 'CLOSED' | 'COMPLETED' | 'TERMINATED' | 'REJECTED';

export type CycleStatus =
  | 'SCHEDULED' | 'OPEN' | 'AUCTION_PENDING' | 'AUCTION_DONE'
  | 'DUES_COLLECTED' | 'CLOSED';

// Shape mirrors the staff list endpoint (`GET /staff/v1/chit-groups`), which serializes each
// chit group via `ChitGroupMapper.toResponse` (see chit-group.mapper.ts): the scheme number is
// exposed as `schemeNumber` (the "code" shown throughout the UI) and the chit value in paise as
// `chitValuePaise`.
export interface SchemeListItem {
  id: string;
  schemeNumber: string;
  displayName: string;
  name: string;
  /** Chit value in paise (integer) */
  chitValuePaise: number;
  subscriberCount: number;
  totalTickets: number;
  durationCycles: number;
  cycleFrequency: string;
  auctionType: string;
  status: SchemeStatus;
  currentCycle: number;
  startDate: string | null;
  expectedEndDate: string | null;
  registrarRef: string | null;
  tdsApplicable: boolean;
  gstApplicable: boolean;
  createdAt: string;
  updatedAt: string;
  _count: { subscribers: number };
}

// The detail endpoint (`GET /staff/v1/chit-groups/:id`) returns the raw `chit_groups` entity,
// so it carries the entity column names (`schemeNumber`, `displayName`, `chitValue`) rather than
// the list's mapper shape. This union is also the create/update request shape, so it deliberately
// includes non-column fields the API accepts on write (`auctionDayOfMonth`, `dividendCategory`,
// `hasForemanTicket`, `commissionRate`, `maxBidDiscountRate`).
export interface SchemeDetail extends Omit<SchemeListItem, 'chitValuePaise' | 'totalTickets' | 'cycleFrequency' | 'auctionType' | 'startDate' | 'expectedEndDate' | 'registrarRef' | 'tdsApplicable' | 'gstApplicable' | 'createdAt' | 'updatedAt'> {
  companyId: string;
  orgUnitId: string | null;
  branchId: string;
  productTypeId: string | null;
  groupType: 'CASH' | 'COMMODITY' | 'DIGITAL';
  displayName: string;
  chitValue: number;
  durationCycles: number;
  cycleFrequency: 'MONTHLY' | 'FORTNIGHTLY' | 'WEEKLY';
  auctionType: 'OPEN' | 'CLOSED';
  tdsApplicable: boolean;
  gstApplicable: boolean;
  registrarRef: string | null;
  registeredAt: string | null;
  startDate: string | null;
  expectedEndDate: string | null;
  currentCycle: number;
  createdBy: string;
  biddingWindowMinutes: number | null;
  commissionRate: number | null;
  maxBidDiscountRate: number | null;
  dividendCategory: 'ESTIMATE_BASED' | 'AUCTION_BASED' | 'DRAW_BASED' | null;
  hasForemanTicket: boolean;
  auctionDayOfMonth?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CycleListItem {
  id: string;
  cycleNumber: number;
  scheduledDate: string;
  status: CycleStatus;
  auctionWinner: string | null;
  totalCollected: number | null;
  auctionId: string | null;
}

export interface CycleDetail extends CycleListItem {
  openedAt: string | null;
  closedAt: string | null;
  auctionId: string | null;
}

export const schemeKeys = {
  all: ['schemes'] as const,
  lists: () => [...schemeKeys.all, 'list'] as const,
  list: (params: Record<string, unknown> | undefined) => [...schemeKeys.lists(), params] as const,
  details: () => [...schemeKeys.all, 'detail'] as const,
  detail: (id: string) => [...schemeKeys.details(), id] as const,
  cycles: (id: string) => [...schemeKeys.detail(id), 'cycles'] as const,
  cycle: (id: string, num: number) => [...schemeKeys.cycles(id), num] as const,
};

export function useSchemes(params?: { page?: number; limit?: number; status?: string[]; branch?: string }) {
  return useQuery({
    queryKey: schemeKeys.list(params as Record<string, unknown>),
    queryFn: async () => {
      const searchParams = params ? new URLSearchParams(params as unknown as Record<string, string>).toString() : '';
      const response = await fetcher.get<{ data: SchemeListItem[]; meta: Record<string, unknown> }>(`/staff/v1/chit-groups?${searchParams}`);
      return response;
    },
  });
}

export function useScheme(id: string) {
  return useQuery({
    queryKey: schemeKeys.detail(id),
    queryFn: async () => {
      const response = await fetcher.get<SchemeDetail>(`/staff/v1/chit-groups/${id}`);
      return response;
    },
    enabled: !!id,
  });
}

export function useCreateScheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { chitTypeTemplateId: string; branchId: string; displayName: string; productTypeId?: string; auctionDayOfMonth: number }) =>
      fetcher.post<{ data: SchemeDetail }>('/staff/v1/chit-groups', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
    },
  });
}


export function useActivateScheme(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { startDate: string; auctionDayOfMonth: number }) =>
      fetcher.post<{ data: SchemeDetail }>(`/staff/v1/chit-groups/${id}/activate`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(id) });
    },
  });
}

export function useSchemeCycles(id: string) {
  return useQuery({
    queryKey: schemeKeys.cycles(id),
    queryFn: async () => {
      const response = await fetcher.get<{ data: CycleListItem[]; meta: Record<string, unknown> }>(`/staff/v1/chit-groups/${id}/cycles`);
      return response.data;
    },
    enabled: !!id,
  });
}

export interface SubscriberTicket {
  id: string;
  ticketNumber: number;
  status: string;
}

export interface SubscriberListItem {
  id: string;
  schemeId: string;
  customerId: string;
  ticketNumbers: number[];
  tickets?: SubscriberTicket[];
  status: 'ACTIVE' | 'PRIZED' | 'DEFAULTED' | 'EXITED' | 'SUSPENDED';
  enrolledBy: string;
  enrolledAt: string;
  prizedAt: string | null;
  exitAt: string | null;
  notes: string | null;
  customerName?: string;
  customerMobile?: string;
  schemeName?: string;
  schemeNumber?: string;
}

export interface EnrollSubscriberPayload {
  customerId: string;
  ticketNumbers: number[];
}

export const subscriberKeys = {
  all: ['subscribers'] as const,
  lists: () => [...subscriberKeys.all, 'list'] as const,
  list: (schemeId: string, params: Record<string, unknown> | undefined) => [...subscriberKeys.lists(), schemeId, params] as const,
};

export function useSchemeSubscribers(schemeId: string, params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: subscriberKeys.list(schemeId, params as Record<string, unknown>),
    queryFn: async () => {
      const searchParams = params ? new URLSearchParams(params as unknown as Record<string, string>).toString() : '';
      const response = await fetcher.get<{ data: SubscriberListItem[]; meta: Record<string, unknown> }>(
        `/staff/v1/chit-groups/${schemeId}/subscribers?${searchParams}`
      );
      return response;
    },
    enabled: !!schemeId,
  });
}

export function useEnrollSubscriber(schemeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: EnrollSubscriberPayload) =>
      fetcher.post<{ id: string }>(`/staff/v1/chit-groups/${schemeId}/subscribers`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(schemeId) });
    },
  });
}

export function useAdmitSubscriber(schemeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (subscriptionId: string) =>
      fetcher.post<{ id: string }>(`/staff/v1/subscriptions/${subscriptionId}/admit`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(schemeId) });
    },
  });
}

export { fetcher };

export function useSubscriberDetailed(schemeId: string, subscriberId: string | null) {
  return useQuery({
    queryKey: ['schemes', schemeId, 'subscribers', subscriberId, 'detailed'],
    queryFn: () => subscriberId ? fetcher.get<Record<string, unknown>>(`/staff/v1/chit-groups/${schemeId}/subscribers/${subscriberId}/detailed`) : null,
    enabled: !!subscriberId,
  });
}

export function useMarkDeceased(schemeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ subscriberId, notes }: { subscriberId: string; notes?: string }) =>
      fetcher.post<{ success: boolean }>(`/staff/v1/chit-groups/${schemeId}/subscribers/${subscriberId}/deceased`, { notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['schemes', schemeId, 'subscribers'] });
    },
  });
}

export interface LedgerEntry {
  id: string;
  date: string;
  description: string;
  entryType: string;
  debitPaise: number;
  creditPaise: number;
  balancePaise: number;
  sourceId: string;
}

export interface SchemeLedgerResponse {
  entries: LedgerEntry[];
  openingBalancePaise: number;
  closingBalancePaise: number;
}

export interface SchemeLedgerSummary {
  totalCollectedPaise: number;
  totalDisbursedPaise: number;
  totalDividendsPaise: number;
  commissionEarnedPaise: number;
  reserveBalancePaise: number;
}

export function useSchemeLedger(schemeId: string) {
  return useQuery({
    queryKey: ['schemes', schemeId, 'ledger'],
    queryFn: async () => {
      const response = await fetcher.get<SchemeLedgerResponse>(`/staff/v1/chit-groups/${schemeId}/ledger`);
      return response;
    },
    enabled: !!schemeId,
  });
}

export function useSchemeLedgerSummary(schemeId: string) {
  return useQuery({
    queryKey: ['schemes', schemeId, 'ledger', 'summary'],
    queryFn: async () => {
      const response = await fetcher.get<SchemeLedgerSummary>(`/staff/v1/chit-groups/${schemeId}/ledger/summary`);
      return response;
    },
    enabled: !!schemeId,
  });
}

export interface SchemeDocument {
  id: string;
  schemeId: string;
  name: string;
  documentType: string;
  category: string;
  fileUrl: string;
  mimeType: string;
  sizeBytes: number;
  checksum: string;
  version: number;
  supersedesDocumentId: string | null;
  effectiveDate: string | null;
  expiryDate: string | null;
  isMandatory: boolean;
  complianceDeadline: string | null;
  tags: string[] | null;
  uploadedBy: string;
  source: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface SchemeDocumentsResponse {
  items: SchemeDocument[];
  total: number;
  page: number;
  limit: number;
}

export function useSchemeDocuments(schemeId: string, params?: { page?: number; limit?: number; documentType?: string; category?: string }) {
  return useQuery({
    queryKey: ['schemes', schemeId, 'documents', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.set('page', String(params.page));
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.documentType) searchParams.set('documentType', params.documentType);
      if (params?.category) searchParams.set('category', params.category);
      const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';
      const response = await fetcher.get<SchemeDocumentsResponse>(`/staff/v1/chit-groups/${schemeId}/documents${queryString}`);
      return response;
    },
    enabled: !!schemeId,
  });
}

export function useCreateSchemeDocument(schemeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<SchemeDocument, 'id' | 'schemeId' | 'createdAt' | 'updatedAt' | 'version' | 'status' | 'uploadedBy' | 'supersedesDocumentId'>) =>
      fetcher.post<{ data: SchemeDocument }>(`/staff/v1/chit-groups/${schemeId}/documents`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemes', schemeId, 'documents'] });
    },
  });
}

export function useUpdateSchemeDocument(schemeId: string, documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<SchemeDocument>) =>
      fetcher.put<{ data: SchemeDocument }>(`/staff/v1/chit-groups/${schemeId}/documents/${documentId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemes', schemeId, 'documents'] });
    },
  });
}

export function useDeleteSchemeDocument(schemeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => fetcher.post<{ success: boolean }>(`/staff/v1/chit-groups/${schemeId}/documents/${documentId}/delete`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemes', schemeId, 'documents'] });
    },
  });
}

export function useCreateDocumentVersion(schemeId: string, documentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Omit<SchemeDocument, 'id' | 'schemeId' | 'createdAt' | 'updatedAt' | 'version' | 'status' | 'supersedesDocumentId'> & { supersedesDocumentId: string }) =>
      fetcher.post<{ data: SchemeDocument }>(`/staff/v1/chit-groups/${schemeId}/documents/${documentId}/versions`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemes', schemeId, 'documents'] });
    },
  });
}

export function useUpdateCycleAuctionDay(schemeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ cycleNumber, auctionDayOfMonth }: { cycleNumber: number; auctionDayOfMonth: number }) =>
      fetcher.put<{ success: boolean }>(`/staff/v1/chit-groups/${schemeId}/cycles/${cycleNumber}`, { auctionDayOfMonth }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemes', 'detail', schemeId] });
      queryClient.invalidateQueries({ queryKey: ['schemes', 'detail', schemeId, 'cycles'] });
    },
  });
}

// Migrated from the now-removed features/schemes/hooks.ts duplicate layer (see
// docs/audits/2026-07-22-scheme-finance-reports-audit.md §2.1/§6.2) -- these had no equivalent
// here, unlike useSchemes/useActivateScheme which existed in both and were reconciled in favor
// of this file's version at each of their call sites.

export function useUpdateScheme(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<SchemeDetail>) => fetcher.put<{ data: SchemeDetail }>(`/staff/v1/chit-groups/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(id) });
    },
  });
}

export function useSubmitScheme(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetcher.post<{ data: SchemeDetail }>(`/staff/v1/chit-groups/${id}/submit`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(id) });
    },
  });
}

export function useSuspendScheme(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) => fetcher.post<{ data: SchemeDetail }>(`/staff/v1/chit-groups/${id}/suspend`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(id) });
    },
  });
}

export function useResumeScheme(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetcher.post<{ data: SchemeDetail }>(`/staff/v1/chit-groups/${id}/resume`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(id) });
    },
  });
}

export function useCloseScheme(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetcher.post<{ data: SchemeDetail }>(`/staff/v1/chit-groups/${id}/close`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(id) });
    },
  });
}

export function useMarkSchemeReady(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetcher.post<{ data: SchemeDetail }>(`/staff/v1/chit-groups/${id}/ready`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(id) });
    },
  });
}

export function useAdvancePeriod(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetcher.post<{ data: SchemeDetail }>(`/staff/v1/chit-groups/${id}/advance-period`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: schemeKeys.cycles(id) });
    },
  });
}

export function useCompleteScheme(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => fetcher.post<{ data: SchemeDetail }>(`/staff/v1/chit-groups/${id}/complete`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(id) });
    },
  });
}

export function useTerminateScheme(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { reason: string }) => fetcher.post<{ data: SchemeDetail }>(`/staff/v1/chit-groups/${id}/terminate`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: schemeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: schemeKeys.detail(id) });
    },
  });
}

export function useNameTransfers(filters?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['schemes', 'name-transfers', filters],
    queryFn: () => {
      const params = filters ? '?' + new URLSearchParams(filters as Record<string, string>).toString() : '';
      return fetcher.get<{ data: unknown[]; meta: unknown }>(`/staff/v1/chit-groups/transfers${params}`);
    },
  });
}

export interface CreateSubscriberTransferPayload {
  subscriberId: string;
  newCustomerId: string;
  valuePaise?: number;
  remarks?: string;
}

export function useCreateSubscriberTransfer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSubscriberTransferPayload) => fetcher.post<{ data: unknown }>('/staff/v1/chit-groups/transfers', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemes', 'name-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['approvals', 'list'] });
    },
  });
}
